/**
 * Núcleo PURO del puente del host (A3, ADR 0003, refs loomitz/iris#24).
 *
 * Sin postMessage, sin DOM, sin red: sólo las reglas del contrato
 * (`tools/puente-schemas.json`) que el transporte (`bridge.ts`) envuelve. Todo
 * lo testeable-sin-navegador vive aquí:
 *
 *   - allowlist EFECTIVA del bridge = ConfigHost.comandos ∩ implementados
 *     (fail-closed; el shell la vuelve a estrechar con ConfigSitio).
 *   - proyección FAIL-CLOSED del snapshot del carrito a la allowlist estricta
 *     de print `cart-snapshot.ts` (campos fuera de allowlist se descartan;
 *     jamás se reenvía de más).
 *   - despacho tipado de comandos contra el handler-map (la ÚNICA fuente de
 *     "implementados", cruzada contra COMANDOS_V1 por un test del bridge).
 *
 * Códigos de error: los del `$defs/CodigosConocidos`. Los comandos de tour
 * (`tour_*`, `ancla_resaltar`) VALIDAN su payload aquí (puro) y DELEGAN en
 * `ctx.motorTours` (A4, `tour-engine.ts`); sin motor cableado responden
 * `capacidad_no_configurada` (el seam que dejó A3 sigue vivo para un bridge sin
 * `toursUrl` o con catálogo inválido — fail-closed).
 */
import {
  COMANDOS_CONOCIDOS,
  CAPACIDADES_CONOCIDAS,
  PROTO,
  PUENTE_V,
  type AnclaResaltarPayload,
  type CartSnapshot,
  type CartSnapshotAcabados,
  type CartSnapshotItem,
  type CartSnapshotMedidas,
  type CartSnapshotOpciones,
  type ComandoNombre,
  type Comando,
  type ConfigHost,
  type CotizadorCargarSeleccionPayload,
  type CarritoAgregarSeleccionPayload,
  type DiagramaAbrirModalPayload,
  type ErrorPuenteDetalle,
  type Respuesta,
  type SeleccionCotizable,
  type TourAvanzarPayload,
  type TourCancelarPayload,
  type TourCorrerPayload,
  type WidgetPresentarPayload,
  type WidgetPresentarRespuesta,
  type WidgetOcultarRespuesta,
} from "./tipos"
import type { MotorTours, ResultadoTour } from "./tour-engine"

export const COMANDOS_POR_CAPACIDAD = {
  "cotizador@1": ["cotizador_cargar_seleccion"],
  "carrito@1": ["carrito_snapshot", "carrito_agregar_seleccion", "carrito_refrescar"],
  "diagrama@1": ["diagrama_abrir_modal"],
  "tours@1": ["tour_correr", "tour_avanzar", "tour_cancelar", "ancla_resaltar"],
  "widget@1": ["widget_presentar", "widget_ocultar"],
} as const

// --- Builders de respuesta (siempre sobre válido: proto+v+tipo+id+comando) ---

export function respuestaOk(
  id: string,
  comando: ComandoNombre,
  payload: object
): Respuesta {
  return { proto: PROTO, v: PUENTE_V, tipo: "respuesta", id, comando, ok: true, payload }
}

export function respuestaError(
  id: string,
  comando: ComandoNombre,
  codigo: string,
  detalle?: ErrorPuenteDetalle
): Respuesta {
  const error = detalle ? { codigo, detalle } : { codigo }
  return { proto: PROTO, v: PUENTE_V, tipo: "respuesta", id, comando, ok: false, error }
}

// --- Allowlist / implementados ---------------------------------------------

/** Nombres realmente implementados por este bridge = llaves del handler-map. */
export function comandosImplementados(): ComandoNombre[] {
  return Object.keys(HANDLERS)
}

/** ¿El nombre tiene handler registrado en este bridge? */
export function esComandoImplementado(nombre: string): boolean {
  return Object.prototype.hasOwnProperty.call(HANDLERS, nombre)
}

export function comandosDeCapacidades(capacidades: readonly string[] = []): ComandoNombre[] {
  const comandos: ComandoNombre[] = []
  for (const capacidad of capacidades) {
    const lista = COMANDOS_POR_CAPACIDAD[capacidad as keyof typeof COMANDOS_POR_CAPACIDAD]
    if (!lista) continue
    for (const comando of lista) {
      if (!comandos.includes(comando)) comandos.push(comando)
    }
  }
  return comandos
}

export function comandosDeclarados(configHost: ConfigHost): ComandoNombre[] {
  const comandos: ComandoNombre[] = []
  for (const comando of configHost.comandos ?? []) {
    if (!comandos.includes(comando)) comandos.push(comando)
  }
  for (const comando of comandosDeCapacidades(configHost.capacidades)) {
    if (!comandos.includes(comando)) comandos.push(comando)
  }
  return comandos
}

export function capacidadesEfectivas(configHost: ConfigHost, comandos: readonly ComandoNombre[]): string[] {
  const declaradas = configHost.capacidades ?? []
  const permitidos = new Set(comandos)
  const out: string[] = []
  for (const capacidad of declaradas) {
    const lista = COMANDOS_POR_CAPACIDAD[capacidad as keyof typeof COMANDOS_POR_CAPACIDAD]
    if (!lista) continue
    if (lista.some((comando) => permitidos.has(comando)) && !out.includes(capacidad)) {
      out.push(capacidad)
    }
  }
  return out
}

/**
 * Allowlist EFECTIVA del bridge = ConfigHost.comandos ∩ implementados. Un
 * nombre pedido por el host que este bridge no implementa NO consiente nada:
 * se descarta y se reporta como aviso (el schema lo documenta como
 * "console.warn accionable"). Es lo que el bridge anuncia en `hola_puente`.
 */
export function allowlistEfectiva(
  configHost: ConfigHost,
  onAviso?: (mensaje: string, nombre: string) => void
): ComandoNombre[] {
  const efectiva: ComandoNombre[] = []
  for (const capacidad of configHost.capacidades ?? []) {
    if (!(CAPACIDADES_CONOCIDAS as readonly string[]).includes(capacidad)) {
      onAviso?.(
        `ConfigHost.capacidades incluye '${capacidad}', que este bridge no conoce: se ignora`,
        capacidad
      )
    }
  }
  for (const nombre of comandosDeclarados(configHost)) {
    if (esComandoImplementado(nombre)) {
      if (!efectiva.includes(nombre)) efectiva.push(nombre)
    } else {
      onAviso?.(
        `ConfigHost.comandos incluye '${nombre}', que este bridge no implementa: se ignora`,
        nombre
      )
    }
  }
  return efectiva
}

// --- Proyección FAIL-CLOSED del snapshot del carrito ------------------------

const CLAVES_ACABADOS = ["laminado", "spray", "montaje"] as const

function esStringNoVacio(x: unknown): x is string {
  return typeof x === "string" && x.length > 0
}
function esNumeroFinito(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x)
}

function proyectarMedidas(raw: unknown): CartSnapshotMedidas | undefined {
  if (typeof raw !== "object" || raw === null) return undefined
  const { ancho, largo, unidad } = raw as Record<string, unknown>
  if (!esNumeroFinito(ancho) || ancho <= 0) return undefined
  if (!esNumeroFinito(largo) || largo <= 0) return undefined
  if (!esStringNoVacio(unidad)) return undefined
  return { ancho, largo, unidad }
}

function proyectarAcabados(raw: unknown): CartSnapshotAcabados | undefined {
  if (typeof raw !== "object" || raw === null) return undefined
  const src = raw as Record<string, unknown>
  const out: CartSnapshotAcabados = {}
  // `margen` NO está en CLAVES_ACABADOS → se descarta por construcción.
  for (const k of CLAVES_ACABADOS) {
    const v = src[k]
    if (esStringNoVacio(v)) out[k] = v
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function proyectarOpciones(raw: unknown): CartSnapshotOpciones | undefined {
  if (typeof raw !== "object" || raw === null) return undefined
  const src = raw as Record<string, unknown>
  const out: CartSnapshotOpciones = {}
  if (typeof src.bastidor === "boolean") out.bastidor = src.bastidor
  if (typeof src.barniz === "boolean") out.barniz = src.barniz
  return out.bastidor !== undefined || out.barniz !== undefined ? out : undefined
}

/**
 * Proyecta UN item crudo del proveedorCarrito a la allowlist estricta. Copia
 * SÓLO las llaves permitidas por `tipo` (papel↮canvas) y descarta lo demás
 * (PII, precios, `margen`, selectores, cualquier llave fuera de allowlist).
 * `null` si el item no tiene un `tipo` reconocible.
 */
export function proyectarCartItem(raw: unknown): CartSnapshotItem | null {
  if (typeof raw !== "object" || raw === null) return null
  const src = raw as Record<string, unknown>
  if (src.tipo !== "papel" && src.tipo !== "canvas") return null

  const item: CartSnapshotItem = { tipo: src.tipo }
  if (esStringNoVacio(src.productKind)) item.productKind = src.productKind
  if (esStringNoVacio(src.rollo)) item.rollo = src.rollo
  if (esNumeroFinito(src.piezas) && src.piezas > 0) {
    item.piezas = Math.trunc(src.piezas)
  }
  const medidas = proyectarMedidas(src.medidas)
  if (medidas) item.medidas = medidas

  if (src.tipo === "papel") {
    const acabados = proyectarAcabados(src.acabados)
    if (acabados) item.acabados = acabados
  } else {
    if (esStringNoVacio(src.madera)) item.madera = src.madera
    if (esStringNoVacio(src.canto)) item.canto = src.canto
    if (esStringNoVacio(src.barniz)) item.barniz = src.barniz
    if (esStringNoVacio(src.acabadoCanto)) item.acabadoCanto = src.acabadoCanto
    const opciones = proyectarOpciones(src.opciones)
    if (opciones) item.opciones = opciones
  }

  return item
}

/** Cap de items (espeja MAX_CART_SNAPSHOT_ITEMS de print cart-snapshot.ts). */
export const MAX_CART_SNAPSHOT_ITEMS = 20

/**
 * Proyecta la salida cruda del proveedorCarrito a un CartSnapshot saneado.
 * Acepta `{ items: [...] }` o directamente un arreglo de items. Aplica el cap y
 * descarta lo no-reconstruible. `null` = carrito vacío / nada válido → el
 * bridge responde `cart:null` (el shell traduce a undefined conservando shape).
 */
export function proyectarCartSnapshot(raw: unknown): CartSnapshot | null {
  let lista: unknown[]
  if (Array.isArray(raw)) {
    lista = raw
  } else if (typeof raw === "object" && raw !== null && Array.isArray((raw as { items?: unknown }).items)) {
    lista = (raw as { items: unknown[] }).items
  } else {
    return null
  }

  const items: CartSnapshotItem[] = []
  for (const crudo of lista) {
    if (items.length >= MAX_CART_SNAPSHOT_ITEMS) break
    const item = proyectarCartItem(crudo)
    if (item) items.push(item)
  }
  return items.length > 0 ? { items } : null
}

// --- Validación mínima de payloads de comando -------------------------------

function payloadWidgetValido(payload: object): payload is WidgetPresentarPayload {
  const src = payload as Record<string, unknown>
  const claves = Object.keys(src)
  if (claves.length === 0) return false // minProperties: 1
  for (const k of claves) {
    if (k === "estado") {
      if (src.estado !== "abierto" && src.estado !== "cerrado") return false
    } else if (k === "tamano") {
      if (src.tamano !== "compacto" && src.tamano !== "amplio") return false
    } else {
      return false // additionalProperties: false — sin píxeles ni llaves extra
    }
  }
  return true
}

function esSeleccionValida(value: unknown): value is SeleccionCotizable {
  if (typeof value !== "object" || value === null) return false
  const src = value as Record<string, unknown>
  if (src.tipo !== "papel" && src.tipo !== "canvas") return false
  if (!esStringNoVacio(src.productKind) || !esStringNoVacio(src.rollo)) return false
  const medidas = src.medidas
  if (typeof medidas !== "object" || medidas === null) return false
  const m = medidas as Record<string, unknown>
  if (!esNumeroFinito(m.ancho) || m.ancho <= 0) return false
  if (!esNumeroFinito(m.largo) || m.largo <= 0) return false
  if (!esStringNoVacio(m.unidad)) return false
  if ("piezas" in src && src.piezas !== undefined) {
    if (!esNumeroFinito(src.piezas) || src.piezas < 1) return false
  }
  return true
}

function validarCotizadorPayload(p: object): CotizadorCargarSeleccionPayload | null {
  const src = p as Record<string, unknown>
  if (!esSeleccionValida(src.seleccion)) return null
  if ("abrir" in src && src.abrir !== undefined && typeof src.abrir !== "boolean") return null
  for (const k of Object.keys(src)) if (k !== "seleccion" && k !== "abrir") return null
  return { seleccion: src.seleccion, abrir: src.abrir === true }
}

function validarCarritoAgregarPayload(p: object): CarritoAgregarSeleccionPayload | null {
  const src = p as Record<string, unknown>
  if (!esSeleccionValida(src.seleccion)) return null
  if (!esNumeroFinito(src.piezas) || src.piezas < 1) return null
  for (const k of Object.keys(src)) if (k !== "seleccion" && k !== "piezas") return null
  return { seleccion: src.seleccion, piezas: Math.trunc(src.piezas) }
}

function validarDiagramaPayload(p: object): DiagramaAbrirModalPayload | null {
  const src = p as Record<string, unknown>
  if (typeof src.diagram !== "object" || src.diagram === null || Array.isArray(src.diagram)) {
    return null
  }
  for (const k of Object.keys(src)) if (k !== "diagram") return null
  return { diagram: src.diagram as object }
}

// --- Contexto de despacho ---------------------------------------------------

export interface DespachoCtx {
  /** ConfigHost.comandos ∩ implementados (lo mismo anunciado en hola_puente). */
  allowlistEfectiva: ReadonlySet<ComandoNombre>
  /** Callback del host; ausente ⇒ carrito_snapshot responde capacidad_no_configurada. */
  proveedorCarrito?: () => unknown
  /** Lee y reconcilia el carrito servido con las credenciales del host. */
  refrescarCarrito?: () => boolean | void | Promise<boolean | void>
  /** Aplica los presets al iframe y devuelve el estado EFECTIVO (sin píxeles). */
  presentar?: (payload: WidgetPresentarPayload) => WidgetPresentarRespuesta
  /** Retira el iframe tras una preferencia personal persistida. */
  ocultar?: () => boolean | void
  /**
   * Motor de tours (A4). Ausente ⇒ los comandos de tour responden
   * `capacidad_no_configurada` (el bridge no cargó/validó `toursUrl` — mismo seam
   * que dejó A3). Presente ⇒ el handler valida el payload y delega.
   */
  motorTours?: MotorTours
  cargarSeleccionCotizador?: (payload: CotizadorCargarSeleccionPayload) => unknown
  agregarSeleccionCarrito?: (payload: CarritoAgregarSeleccionPayload) => unknown
  abrirModalDiagrama?: (payload: DiagramaAbrirModalPayload) => unknown
}

type Handler = (cmd: Comando, ctx: DespachoCtx) => Respuesta | Promise<Respuesta>

// --- Validación de payloads de tour (pura; el motor re-valida contra su DOM) --

/** `$defs/TourId`. */
const RE_TOUR_ID = /^[a-z0-9-]+\.[a-z0-9][a-z0-9.-]*$/
/** `$defs/AnclaIris` — pattern anti-selector. */
const RE_ANCLA = /^[a-z0-9]+(-[a-z0-9]+)*$/

function esTourIdValido(x: unknown): x is string {
  return typeof x === "string" && x.length <= 64 && RE_TOUR_ID.test(x)
}
function esPasoValido(x: unknown): x is number {
  return typeof x === "number" && Number.isInteger(x) && x >= 1
}

function validarTourCorrer(p: object): TourCorrerPayload | null {
  const src = p as Record<string, unknown>
  if (!esTourIdValido(src.tourId)) return null
  if ("paso" in src && src.paso !== undefined && !esPasoValido(src.paso)) return null
  for (const k of Object.keys(src)) if (k !== "tourId" && k !== "paso") return null
  return "paso" in src && src.paso !== undefined
    ? { tourId: src.tourId, paso: src.paso as number }
    : { tourId: src.tourId }
}

function validarTourAvanzar(p: object): TourAvanzarPayload | null {
  const src = p as Record<string, unknown>
  if (!esTourIdValido(src.tourId) || !esPasoValido(src.paso)) return null
  for (const k of Object.keys(src)) if (k !== "tourId" && k !== "paso") return null
  return { tourId: src.tourId, paso: src.paso }
}

function validarTourCancelar(p: object): TourCancelarPayload | null {
  const src = p as Record<string, unknown>
  if (!esTourIdValido(src.tourId)) return null
  for (const k of Object.keys(src)) if (k !== "tourId") return null
  return { tourId: src.tourId }
}

function validarAnclaResaltar(p: object): AnclaResaltarPayload | null {
  const src = p as Record<string, unknown>
  if (!esTourIdValido(src.tourId) || !esPasoValido(src.paso)) return null
  if (typeof src.ancla !== "string" || src.ancla.length > 64 || !RE_ANCLA.test(src.ancla)) return null
  for (const k of Object.keys(src)) if (k !== "tourId" && k !== "paso" && k !== "ancla") return null
  return { tourId: src.tourId, paso: src.paso, ancla: src.ancla }
}

/** Envuelve un {@link ResultadoTour} del motor en la Respuesta tipada del sobre. */
function envolver(cmd: Comando, res: ResultadoTour): Respuesta {
  return res.ok
    ? respuestaOk(cmd.id, cmd.comando, res.payload)
    : respuestaError(cmd.id, cmd.comando, res.codigo, res.detalle)
}

/**
 * Fábrica de handlers de tour: capacidad_no_configurada si el bridge no tiene
 * motor (seam A3), payload_invalido si el payload no cumple el `$def`, y delega
 * al motor en otro caso. `validar` es el guard puro por comando; `correr` invoca
 * el método del motor con el payload YA validado.
 */
function handlerTour<P extends object>(
  validar: (p: object) => P | null,
  correr: (motor: MotorTours, payload: P) => ResultadoTour
): Handler {
  return (cmd, ctx) => {
    if (!ctx.motorTours) {
      return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
    }
    const payload = validar(cmd.payload)
    if (!payload) return respuestaError(cmd.id, cmd.comando, "payload_invalido")
    return envolver(cmd, correr(ctx.motorTours, payload))
  }
}

async function handlerCarrito(cmd: Comando, ctx: DespachoCtx): Promise<Respuesta> {
  if (!ctx.proveedorCarrito) {
    return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
  }
  let crudo: unknown
  try {
    crudo = await ctx.proveedorCarrito()
  } catch {
    return respuestaError(cmd.id, cmd.comando, "error_interno")
  }
  const cart = proyectarCartSnapshot(crudo)
  return respuestaOk(cmd.id, cmd.comando, { cart })
}

async function handlerCarritoRefrescar(cmd: Comando, ctx: DespachoCtx): Promise<Respuesta> {
  if (Object.keys(cmd.payload).length !== 0) {
    return respuestaError(cmd.id, cmd.comando, "payload_invalido")
  }
  if (!ctx.refrescarCarrito) {
    return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
  }
  try {
    const resultado = await ctx.refrescarCarrito()
    if (resultado === false) return respuestaError(cmd.id, cmd.comando, "error_interno")
    return respuestaOk(cmd.id, cmd.comando, { actualizado: true })
  } catch {
    return respuestaError(cmd.id, cmd.comando, "error_interno")
  }
}

function handlerPresentar(cmd: Comando, ctx: DespachoCtx): Respuesta {
  if (!payloadWidgetValido(cmd.payload)) {
    return respuestaError(cmd.id, cmd.comando, "payload_invalido")
  }
  const efectivo = ctx.presentar
    ? ctx.presentar(cmd.payload)
    : aplicarPresetPorDefecto(cmd.payload)
  return respuestaOk(cmd.id, cmd.comando, efectivo)
}

function handlerOcultar(cmd: Comando, ctx: DespachoCtx): Respuesta {
  if (Object.keys(cmd.payload).length !== 0) {
    return respuestaError(cmd.id, cmd.comando, "payload_invalido")
  }
  if (!ctx.ocultar) {
    return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
  }
  try {
    const resultado = ctx.ocultar()
    if (resultado === false) {
      return respuestaError(cmd.id, cmd.comando, "error_interno")
    }
    return respuestaOk(cmd.id, cmd.comando, {
      oculto: true,
    } satisfies WidgetOcultarRespuesta)
  } catch {
    return respuestaError(cmd.id, cmd.comando, "error_interno")
  }
}

function handlerCotizador(cmd: Comando, ctx: DespachoCtx): Respuesta {
  if (!ctx.cargarSeleccionCotizador) {
    return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
  }
  const payload = validarCotizadorPayload(cmd.payload)
  if (!payload) return respuestaError(cmd.id, cmd.comando, "payload_invalido")
  try {
    const resultado = ctx.cargarSeleccionCotizador(payload)
    return respuestaOk(cmd.id, cmd.comando, { aceptada: resultado !== false })
  } catch {
    return respuestaError(cmd.id, cmd.comando, "error_interno")
  }
}

function handlerCarritoAgregar(cmd: Comando, ctx: DespachoCtx): Respuesta {
  if (!ctx.agregarSeleccionCarrito) {
    return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
  }
  const payload = validarCarritoAgregarPayload(cmd.payload)
  if (!payload) return respuestaError(cmd.id, cmd.comando, "payload_invalido")
  try {
    const resultado = ctx.agregarSeleccionCarrito(payload)
    return respuestaOk(cmd.id, cmd.comando, { aceptada: resultado !== false })
  } catch {
    return respuestaError(cmd.id, cmd.comando, "error_interno")
  }
}

function handlerDiagrama(cmd: Comando, ctx: DespachoCtx): Respuesta {
  if (!ctx.abrirModalDiagrama) {
    return respuestaError(cmd.id, cmd.comando, "capacidad_no_configurada")
  }
  const payload = validarDiagramaPayload(cmd.payload)
  if (!payload) return respuestaError(cmd.id, cmd.comando, "payload_invalido")
  try {
    const resultado = ctx.abrirModalDiagrama(payload)
    return respuestaOk(cmd.id, cmd.comando, { abierto: resultado !== false })
  } catch {
    return respuestaError(cmd.id, cmd.comando, "error_interno")
  }
}

/**
 * Eco DOM-less de último recurso (núcleo puro, sin iframe): sólo devuelve el
 * estado efectivo SIN aplicar CSS. En producción NUNCA se llega aquí — el bridge
 * (`bridge.ts`) siempre inyecta `ctx.presentar` = `crearPresentadorIframe`, que
 * sí redimensiona el iframe. Este fallback existe para el camino headless/tests
 * donde no hay elemento que mutar.
 */
function aplicarPresetPorDefecto(
  payload: WidgetPresentarPayload
): WidgetPresentarRespuesta {
  return {
    estado: payload.estado ?? "cerrado",
    tamano: payload.tamano ?? "compacto",
  }
}

/**
 * Handler-map: la FUENTE ÚNICA de "implementados" del bridge. Sus llaves DEBEN
 * coincidir con COMANDOS_V1 (`tools/puente-comandos.mjs`) — lo asevera un test
 * del bridge. Agregar un comando = agregar handler + COMANDOS_V1 + schema.
 */
export const HANDLERS: Record<ComandoConocidoNombre, Handler> = {
  cotizador_cargar_seleccion: handlerCotizador,
  carrito_agregar_seleccion: handlerCarritoAgregar,
  diagrama_abrir_modal: handlerDiagrama,
  carrito_snapshot: handlerCarrito,
  carrito_refrescar: handlerCarritoRefrescar,
  widget_presentar: handlerPresentar,
  widget_ocultar: handlerOcultar,
  tour_correr: handlerTour(validarTourCorrer, (m, p) => m.correr(p)),
  tour_avanzar: handlerTour(validarTourAvanzar, (m, p) => m.avanzar(p)),
  tour_cancelar: handlerTour(validarTourCancelar, (m, p) => m.cancelar(p)),
  ancla_resaltar: handlerTour(validarAnclaResaltar, (m, p) => m.resaltar(p)),
}
type ComandoConocidoNombre = (typeof COMANDOS_CONOCIDOS)[number]

/**
 * Despacha un comando ENTRANTE contra la allowlist efectiva (fail-closed) y
 * devuelve una Respuesta tipada. Orden del contrato:
 *   1. no implementado           → comando_desconocido (detalle {comando})
 *   2. fuera de allowlist efectiva → no_permitido       (detalle {comando})
 *   3. implementado + permitido   → handler (puede devolver capacidad_no_configurada / payload_invalido / ok)
 */
export function despacharComando(cmd: Comando, ctx: DespachoCtx): Respuesta | Promise<Respuesta> {
  const nombre = cmd.comando
  if (!esComandoImplementado(nombre)) {
    return respuestaError(cmd.id, nombre, "comando_desconocido", { comando: nombre })
  }
  if (!ctx.allowlistEfectiva.has(nombre)) {
    return respuestaError(cmd.id, nombre, "no_permitido", { comando: nombre })
  }
  return HANDLERS[nombre as ComandoConocidoNombre](cmd, ctx)
}
